import bs58 from "bs58";

import { SessionKey } from "../types";

/**
 * @hidden
 * Service handling utility operations
 */
export class UtilityService {
  /**
   * @hidden
   * Convert ArrayBuffer to base64url encoding
   *
   * @param buffer - ArrayBuffer containing binary data to encode
   * @returns Base64url encoded string
   *
   * @example
   * ```typescript
   * const binaryData = new Uint8Array([1, 2, 3, 4]).buffer;
   * const encoded = gridClient.bufferToBase64Url(binaryData);
   * console.log(encoded);
   * ```
   *
   * @category Utilities
   */
  bufferToBase64Url(buffer: ArrayBuffer): string {
    const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  /**
   * Helper function to create session key object from string and expiration
   *
   * @param sessionKeyString - Base58-encoded session key string
   * @param expirationInSeconds - Expiration time as a string (seconds since epoch)
   * @returns SessionKey object with decoded key bytes and parsed expiration
   *
   * @example
   * ```typescript
   * const sessionKey = gridClient.getSessionKeyObject(
   *   'base58-encoded-key',
   *   '900'
   * );
   * ```
   *
   * @category Utilities
   */
  getSessionKeyObject(sessionKeyString: string, expirationInSeconds: string): SessionKey {
    const keyBytes = bs58.decode(sessionKeyString);
    const expiration = parseInt(expirationInSeconds, 10);

    // TODO: double check this
    // -expect-error - SessionKey type expects key to be string but we need to send raw bytes array
    const sessionKey = {
        key: Array.from(keyBytes),
        expiration: expiration
    } as SessionKey;

    return sessionKey;
  }

  /**
   * @hidden
   * Convert all object keys from camelCase to snake_case recursively
   *
   * @param obj - Object with camelCase keys
   * @returns Object with snake_case keys
   *
   * @example
   * ```typescript
   * const camelObj = { sessionKey: 'abc', slotNumber: 123 };
   * const snakeObj = utilityService.toSnakeCaseObject(camelObj);
   * // Returns: { session_key: 'abc', slot_number: 123 }
   * ```
   *
   * @category Utilities
   */
  private snakeCase(str: string): string {
    return str
      .replace(/([a-z])([A-Z])/g, '$1_$2')
      .replace(/[\s\-]+/g, '_')
      .toLowerCase();
  }

  private camelCase(str: string): string {
    return str
      .replace(/[_\-\s]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ''))
      .replace(/^[A-Z]/, (c) => c.toLowerCase());
  }

  toSnakeCaseObject(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map(item => this.toSnakeCaseObject(item));
    }

    // Handle plain objects (including null-prototype objects)
    if (typeof obj === 'object' && Object.prototype.toString.call(obj) === '[object Object]') {
      const result: Record<string, any> = {};
      for (const [key, value] of Object.entries(obj)) {
        const snakeKey = this.snakeCase(key);

        // Skip transformation for authentication and session fields to preserve provider enum structure
        if (key === 'authentication' || key === 'session') {
          result[snakeKey] = value;
        } else {
          result[snakeKey] = this.toSnakeCaseObject(value);
        }
      }
      return result;
    }

    // Return primitives as-is
    return obj;
  }

  /**
   * @hidden
   * Convert all object keys from snake_case to camelCase recursively
   *
   * @param obj - Object with snake_case keys
   * @returns Object with camelCase keys
   *
   * @example
   * ```typescript
   * const snakeObj = { session_key: 'abc', slot_number: 123 };
   * const camelObj = utilityService.toCamelCaseObject(snakeObj);
   * // Returns: { sessionKey: 'abc', slotNumber: 123 }
   * ```
   *
   * @category Utilities
   */
  toCamelCaseObject(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map(item => this.toCamelCaseObject(item));
    }

    // Handle plain objects (including null-prototype objects)
    if (typeof obj === 'object' && Object.prototype.toString.call(obj) === '[object Object]') {
      const result: Record<string, any> = {};
      for (const [key, value] of Object.entries(obj)) {
        const camelKey = this.camelCase(key);
        
        // Skip transformation for authentication field to preserve API structure for all providers
        if (key === 'authentication') {
          result[camelKey] = value;
        } else {
          result[camelKey] = this.toCamelCaseObject(value);
        }
      }
      return result;
    }

    // Return primitives as-is
    return obj;
  }
}
